import React, { useState, useEffect, Suspense, useMemo } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Flex,
  Grid,
  GridItem,
  Badge,
  Icon,
  useToast,
  Center,
  Spinner,
  Progress
} from '@chakra-ui/react';
import { FaCalendarAlt, FaCoins, FaTrophy, FaGift, FaCheck, FaLock, FaCaretRight } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SingleFlippingCard from './SingleFlippingCard';

const DailyPrizeCard = () => {
  const toast = useToast();
  const [currentDay, setCurrentDay] = useState(1);
  const [claimed, setClaimed] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [dailyReward, setDailyReward] = useState(0);
  
  // Get day of the week (0 = Sunday, 1 = Monday, etc.)
  const dayOfWeek = useMemo(() => {
    const today = new Date();
    return today.getDay();
  }, []);
  
  // Define card colors for each day of the week
  const cardColors = useMemo(() => [
    { bg: "purple.500", name: "Sunday" },    // Sunday - Purple
    { bg: "red.500", name: "Monday" },       // Monday - Red
    { bg: "yellow.500", name: "Tuesday" },   // Tuesday - Yellow
    { bg: "orange.500", name: "Wednesday" }, // Wednesday - Orange
    { bg: "green.500", name: "Thursday" },   // Thursday - Green
    { bg: "cyan.500", name: "Friday" },      // Friday - Light Blue
    { bg: "blue.500", name: "Saturday" },    // Saturday - Blue
  ], []);
  
  // Get current day's color
  const currentColor = useMemo(() => cardColors[dayOfWeek], [cardColors, dayOfWeek]);
  
  // Check if user has authenticated via password
  const hasPasswordAuth = () => {
    return localStorage.getItem('palmheist_password_bypass') === 'true';
  };
  
  // Simulate user's consecutive days streak
  useEffect(() => {
    // This would normally come from your game state or blockchain
    const savedDay = localStorage.getItem('palmHeistDailyBonusDay');
    const savedClaimed = localStorage.getItem('palmHeistDailyBonusClaimed');
    const lastClaimTime = localStorage.getItem('palmHeistDailyBonusLastClaim');
    
    if (savedDay) {
      setCurrentDay(parseInt(savedDay));
    }
    
    if (savedClaimed) {
      setClaimed(savedClaimed === 'true');
    }
    
    // Calculate time until next claim
    if (lastClaimTime) {
      const nextClaimTime = new Date(parseInt(lastClaimTime) + 24 * 60 * 60 * 1000);
      updateTimeLeft(nextClaimTime);
      
      const interval = setInterval(() => {
        const stillClaimed = updateTimeLeft(nextClaimTime);
        if (!stillClaimed) {
          setClaimed(false);
          clearInterval(interval);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, []);
  
  // Update time left until next claim
  const updateTimeLeft = (nextClaimTime) => {
    const now = new Date();
    const diff = nextClaimTime - now;
    
    if (diff <= 0) {
      setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      return false;
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    setTimeLeft({ hours, minutes, seconds });
    return true;
  };
  
  // Handle claiming daily bonus
  const handleClaim = () => {
    if (claimed) return;
    
    // Generate a random reward between 10-100
    const randomReward = Math.floor(Math.random() * 91) + 10; // 10 to 100
    setDailyReward(randomReward);
    
    // Update state
    setClaimed(true);
    localStorage.setItem('palmHeistDailyBonusClaimed', 'true');
    localStorage.setItem('palmHeistDailyBonusLastClaim', Date.now().toString());
    
    // Update consecutive days
    const newDay = currentDay + 1;
    setCurrentDay(newDay);
    localStorage.setItem('palmHeistDailyBonusDay', newDay.toString());
    
    // Show success message
    toast({
      title: "Daily Bonus Claimed!",
      description: `You received ${randomReward} JINU tokens!`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };
  
  // Generate rewards data for each day
  const generateRewards = () => {
    const rewards = [];
    
    for (let i = 1; i <= 7; i++) {
      rewards.push({
        day: i,
        amount: i * 10,
        claimed: i < currentDay,
        current: i === currentDay
      });
    }
    
    return rewards;
  };
  
  const rewards = generateRewards();
  
  return (
    <Box 
      p={4} 
      borderRadius="md" 
      bg="linear-gradient(135deg, rgba(10, 20, 15, 0.7) 0%, rgba(5, 30, 20, 0.8) 100%)"
      backdropFilter="blur(10px)"
      border="1px solid rgba(0, 230, 118, 0.2)"
      boxShadow="0 8px 32px rgba(0, 230, 118, 0.15)"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'radial-gradient(circle at top right, rgba(0, 230, 118, 0.05), transparent 60%)',
        pointerEvents: 'none',
      }}
    >
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
        {/* Left side - Flipping Card */}
        <GridItem>
          <Center p={4} h="100%">
            <Suspense fallback={<Spinner size="xl" color="green.500" />}>
              <Box width="300px" height="400px">
                <SingleFlippingCard 
                  symbol="Star"
                  isBonus={true}
                  cardColor={currentColor.bg}
                  onBonusComplete={(amount) => {
                    // Generate a random reward between 10-100
                    const randomReward = Math.floor(Math.random() * 91) + 10; // 10 to 100
                    setDailyReward(randomReward);
                    
                    toast({
                      title: "Daily Prize Won!",
                      description: `You received ${randomReward} JINU tokens!`,
                      status: "success",
                      duration: 3000,
                      isClosable: true,
                    });
                  }}
                />
              </Box>
            </Suspense>
          </Center>
        </GridItem>
        
        {/* Right side - Controls and Days */}
        <GridItem>
          <VStack spacing={4} align="stretch" h="100%" justify="space-between">
            <Flex justify="space-between" align="center">
              <HStack>
                <Icon as={FaGift} color="#00e676" boxSize={5} />
                <Text fontSize="xl" fontWeight="bold" color="#00e676">Daily Prize Card</Text>
              </HStack>
              <Badge colorScheme="green" p={1} borderRadius="md">
                {currentColor.name}
              </Badge>
            </Flex>
            
            <Text color="gray.300" fontSize="sm">
              Flip the card to reveal your daily prize! You can win between 10-100 JINU tokens each day.
            </Text>
            
            <HStack spacing={4} mt={4}>
              <Box
                p={2}
                borderRadius="md"
                bg="rgba(0, 20, 10, 0.4)"
                backdropFilter="blur(5px)"
                border="1px solid rgba(50, 70, 60, 0.2)"
                w="100%"
              >
                <Flex justify="space-between" align="center">
                  <Text color="gray.300" fontSize="sm">Today's Card Color:</Text>
                  <HStack>
                    <Box w="16px" h="16px" borderRadius="full" bg={currentColor.bg} />
                    <Text color="white" fontWeight="bold">{currentColor.name}</Text>
                  </HStack>
                </Flex>
              </Box>
            </HStack>
            
            <Box mt={4}>
              <Text color="gray.300" fontSize="sm" mb={1}>Consecutive Days</Text>
              <Progress 
                value={((currentDay - 1) % 7) / 7 * 100} 
                colorScheme="green"
                borderRadius="full"
                size="sm"
              />
              <Flex justify="space-between" mt={1}>
                <Text color="gray.400" fontSize="xs">Day 1</Text>
                <Text color="gray.400" fontSize="xs">Day 7</Text>
              </Flex>
            </Box>
            
            <Grid templateColumns="repeat(7, 1fr)" gap={2} mt={4}>
              {rewards.map((reward) => (
                <GridItem key={reward.day}>
                  <VStack
                    p={2}
                    borderRadius="md"
                    bg={reward.current ? "rgba(0, 230, 118, 0.15)" : "rgba(0, 20, 10, 0.3)"}
                    backdropFilter="blur(5px)"
                    border="1px solid"
                    borderColor={reward.current ? "rgba(0, 230, 118, 0.4)" : "rgba(50, 70, 60, 0.3)"}
                    opacity={reward.day <= currentDay ? 1 : 0.6}
                  >
                    <Text fontWeight="bold" color="white" fontSize="xs">Day {reward.day}</Text>
                    <HStack spacing={1}>
                      <Icon as={FaCoins} color="#00e676" boxSize={3} />
                      <Text color="white" fontSize="xs">{reward.amount}</Text>
                    </HStack>
                    <Box color={reward.claimed ? "green.400" : reward.current ? "green.400" : "gray.500"} fontSize="xs">
                      {reward.claimed ? (
                        <Icon as={FaCheck} boxSize={3} />
                      ) : reward.current ? (
                        <Icon as={FaGift} boxSize={3} />
                      ) : (
                        <Icon as={FaLock} boxSize={3} />
                      )}
                    </Box>
                  </VStack>
                </GridItem>
              ))}
            </Grid>
            
            {claimed ? (
              <Box p={3} borderRadius="md" bg="rgba(0, 20, 10, 0.4)" backdropFilter="blur(5px)" border="1px solid rgba(50, 70, 60, 0.2)">
                <VStack spacing={2}>
                  <Text color="gray.300">Next bonus available in:</Text>
                  <HStack justify="center" spacing={4}>
                    <VStack>
                      <Text fontSize="xl" fontWeight="bold" color="white">{timeLeft.hours.toString().padStart(2, '0')}</Text>
                      <Text fontSize="xs" color="gray.400">Hours</Text>
                    </VStack>
                    <Text fontSize="xl" color="white">:</Text>
                    <VStack>
                      <Text fontSize="xl" fontWeight="bold" color="white">{timeLeft.minutes.toString().padStart(2, '0')}</Text>
                      <Text fontSize="xs" color="gray.400">Minutes</Text>
                    </VStack>
                    <Text fontSize="xl" color="white">:</Text>
                    <VStack>
                      <Text fontSize="xl" fontWeight="bold" color="white">{timeLeft.seconds.toString().padStart(2, '0')}</Text>
                      <Text fontSize="xs" color="gray.400">Seconds</Text>
                    </VStack>
                  </HStack>
                </VStack>
              </Box>
            ) : (
              <Button
                bg="linear-gradient(135deg, rgba(0, 230, 118, 0.8) 0%, rgba(0, 180, 90, 0.9) 100%)"
                color="white"
                _hover={{
                  bg: "linear-gradient(135deg, rgba(0, 230, 118, 0.9) 0%, rgba(0, 180, 90, 1) 100%)",
                  boxShadow: "0 0 15px rgba(0, 230, 118, 0.4)"
                }}
                leftIcon={<FaGift />}
                onClick={handleClaim}
                size="lg"
                w="full"
                as={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                boxShadow="0 4px 12px rgba(0, 230, 118, 0.2)"
              >
                Claim Daily Prize (10-100 JINU)
              </Button>
            )}
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default DailyPrizeCard;
